export type ModifierEventLike = {
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  getModifierState?: (key: string) => boolean;
} | null | undefined;

export function isAdditiveSelectionGesture(
  originalEvent: ModifierEventLike,
  shiftPressedFallback: boolean,
): boolean {
  const shiftHeld = Boolean(
    originalEvent?.shiftKey
      || originalEvent?.getModifierState?.('Shift')
      || shiftPressedFallback,
  );
  const metaHeld = Boolean(
    originalEvent?.metaKey
      || originalEvent?.getModifierState?.('Meta')
      || originalEvent?.ctrlKey
      || originalEvent?.getModifierState?.('Control'),
  );
  return shiftHeld || metaHeld;
}

function uniqueOrdered(values: Iterable<string>): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const value of values) {
    if (!seen.has(value)) {
      seen.add(value);
      ordered.push(value);
    }
  }
  return ordered;
}

export function applyWaterwayClickSelection(
  currentSelectedWaterwayIds: string[],
  waterwayId: string,
  additive: boolean,
): string[] {
  if (additive) {
    return currentSelectedWaterwayIds.includes(waterwayId)
      ? currentSelectedWaterwayIds.filter((id) => id !== waterwayId)
      : [...currentSelectedWaterwayIds, waterwayId];
  }

  if (
    currentSelectedWaterwayIds.length === 1
    && currentSelectedWaterwayIds[0] === waterwayId
  ) {
    return [];
  }

  return [waterwayId];
}

export function applyWaterwayBoxSelection(
  currentSelectedWaterwayIds: string[],
  hitWaterwayIds: Iterable<string>,
  additive: boolean,
): string[] {
  const hits = uniqueOrdered(hitWaterwayIds);
  if (hits.length === 0) {
    return additive ? currentSelectedWaterwayIds : [];
  }

  if (additive) {
    return uniqueOrdered([...currentSelectedWaterwayIds, ...hits]);
  }

  return hits;
}

export function applyEditorFeatureClickSelection(params: {
  currentSelectedWaterwayIds: string[];
  currentSelectedFeatureId: string | null;
  clickedFeatureId: string;
  clickedFeatureIsWaterway: boolean;
  additive: boolean;
}): {
  selectedWaterwayIds: string[];
  selectedFeatureId: string | null;
} {
  const {
    currentSelectedWaterwayIds,
    currentSelectedFeatureId,
    clickedFeatureId,
    clickedFeatureIsWaterway,
    additive,
  } = params;

  if (!clickedFeatureIsWaterway) {
    return {
      selectedWaterwayIds: additive ? currentSelectedWaterwayIds : [],
      selectedFeatureId: clickedFeatureId,
    };
  }

  const nextSelectedWaterwayIds = applyWaterwayClickSelection(
    currentSelectedWaterwayIds,
    clickedFeatureId,
    additive,
  );

  return {
    selectedWaterwayIds: nextSelectedWaterwayIds,
    selectedFeatureId: nextSelectedWaterwayIds.length === 1 ? nextSelectedWaterwayIds[0] : null,
  };
}

export function applyEditorBoxSelection(params: {
  currentSelectedWaterwayIds: string[];
  hitWaterwayIds: Iterable<string>;
  additive: boolean;
}): {
  selectedWaterwayIds: string[];
  selectedFeatureId: string | null;
} {
  const nextSelectedWaterwayIds = applyWaterwayBoxSelection(
    params.currentSelectedWaterwayIds,
    params.hitWaterwayIds,
    params.additive,
  );

  return {
    selectedWaterwayIds: nextSelectedWaterwayIds,
    selectedFeatureId: nextSelectedWaterwayIds.length === 1 ? nextSelectedWaterwayIds[0] : null,
  };
}

export function applyEditorEmptyClickSelection(params: {
  currentSelectedWaterwayIds: string[];
  currentSelectedFeatureId: string | null;
  additive: boolean;
}): {
  selectedWaterwayIds: string[];
  selectedFeatureId: string | null;
} {
  if (params.additive) {
    return {
      selectedWaterwayIds: params.currentSelectedWaterwayIds,
      selectedFeatureId: params.currentSelectedFeatureId,
    };
  }

  return {
    selectedWaterwayIds: [],
    selectedFeatureId: null,
  };
}
